import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import ProductImageViewer from "../../../routes/pricing-reports/ProductImageViewer";
import { resolveBarcodeFinder } from "../../../services/barcodeFinder";
import {
  formatOmittedProviderLabel,
  getOmittedContextNotes,
  resolveOmittedSourceImageUrl,
} from "../../../services/priceHistoryOmitted";
import MissingStatusChip from "../Missing/MissingStatusChip";
import BarcodeFinderResults from "./BarcodeFinderResults";
import OmissionReasonChip from "./OmissionReasonChip";
import { getOmissionReason } from "./omissionReasons";

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatMoney = (value) => {
  if (value == null || value === "") return "Not available";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return `$${number.toFixed(2)}`;
};

const DetailField = ({ label, children }) => (
  <TableRow>
    <TableCell sx={{ width: "30%", fontWeight: 600, verticalAlign: "top" }}>{label}</TableCell>
    <TableCell sx={{ wordBreak: "break-word" }}>{children ?? "Not available"}</TableCell>
  </TableRow>
);

const PriceHistoryOmittedDetailDialog = ({
  selected,
  taskRun,
  loadingTaskRun = false,
  onClose,
}) => {
  const open = Boolean(selected);
  const reason = getOmissionReason(selected?.code);
  const contextNotes = useMemo(() => getOmittedContextNotes(selected), [selected]);
  const imageUrl = useMemo(
    () =>
      resolveOmittedSourceImageUrl({
        fileId: selected?.file_id,
        taskRun,
      }),
    [selected?.file_id, taskRun]
  );
  const productName = selected?.raw_product_name || "Omitted price record";
  const providerLabel = formatOmittedProviderLabel(selected?.provider);
  const barcodeValue = selected?.raw_barcode || "";

  const [barcodeLookupLoading, setBarcodeLookupLoading] = useState(false);
  const [barcodeLookupError, setBarcodeLookupError] = useState("");
  const [barcodeLookupResult, setBarcodeLookupResult] = useState(null);

  useEffect(() => {
    setBarcodeLookupLoading(false);
    setBarcodeLookupError("");
    setBarcodeLookupResult(null);
  }, [selected?.id]);

  const handleBarcodeLookup = async () => {
    if (!barcodeValue || barcodeLookupLoading) {
      return;
    }

    setBarcodeLookupLoading(true);
    setBarcodeLookupError("");
    setBarcodeLookupResult(null);

    try {
      const result = await resolveBarcodeFinder({
        rawBarcode: barcodeValue,
        includeAllBarcodes: true,
      });
      setBarcodeLookupResult(result);
    } catch (error) {
      setBarcodeLookupError(error.message || "Barcode lookup failed.");
    } finally {
      setBarcodeLookupLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 7 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" component="span">
            {productName}
          </Typography>
          {selected ? <MissingStatusChip status={selected.status} /> : null}
        </Stack>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!selected ? null : (
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <OmissionReasonChip code={selected.code} />
              </Stack>
              {reason.meaning ? (
                <Alert severity="info" sx={{ mb: 0 }}>
                  {reason.meaning}
                </Alert>
              ) : null}
            </Box>

            {loadingTaskRun ? (
              <Typography variant="body2" color="text.secondary">
                Loading photo…
              </Typography>
            ) : null}

            {imageUrl ? (
              <ProductImageViewer
                src={imageUrl}
                alt={`${productName} source photo`}
                maxHeight={280}
              />
            ) : !loadingTaskRun ? (
              <Alert severity="warning">No source photo is available for this record.</Alert>
            ) : null}

            {contextNotes.length ? (
              <Stack spacing={1}>
                {contextNotes.map((note) => (
                  <Alert key={note} severity="warning" variant="outlined">
                    {note}
                  </Alert>
                ))}
              </Stack>
            ) : null}

            <Table size="small" sx={{ border: "1px solid #e0e0e0", backgroundColor: "#fff" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DetailField label="Product">{selected.raw_product_name || "Not available"}</DetailField>
                <DetailField label="Retailer">{selected.raw_retailer || "Not available"}</DetailField>
                <DetailField label="Barcode">
                  {barcodeValue ? (
                    <Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography component="span" sx={{ fontSize: "0.875rem" }}>
                          {barcodeValue}
                        </Typography>
                        <Tooltip title="Look up barcode matches">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Look up barcode matches"
                              onClick={handleBarcodeLookup}
                              disabled={barcodeLookupLoading}
                            >
                              <SearchOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <BarcodeFinderResults
                        loading={barcodeLookupLoading}
                        error={barcodeLookupError}
                        result={barcodeLookupResult}
                      />
                    </Box>
                  ) : (
                    "Not available"
                  )}
                </DetailField>
                <DetailField label="Price">{formatMoney(selected.raw_price)}</DetailField>
                <DetailField label="Unit price">
                  {selected.raw_unit_price != null && selected.raw_unit_price !== ""
                    ? `${formatMoney(selected.raw_unit_price)}${
                        selected.raw_unit ? ` / ${selected.raw_unit}` : ""
                      }`
                    : "Not available"}
                </DetailField>
                <DetailField label="Weight / quantity">
                  {selected.raw_unit_quantity != null && selected.raw_unit_quantity !== ""
                    ? `${selected.raw_unit_quantity}${
                        selected.raw_unit ? ` ${selected.raw_unit}` : ""
                      }`
                    : "Not available"}
                </DetailField>
                <DetailField label="Captured on">{formatDate(selected.record_at)}</DetailField>
                <DetailField label="Logged on">{formatDate(selected.date_created)}</DetailField>
                <DetailField label="Source">{providerLabel || "Not available"}</DetailField>
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PriceHistoryOmittedDetailDialog;
